import { Capacitor } from "@capacitor/core";
import { getToken, deleteToken, onMessage } from "firebase/messaging";
import { doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db, getMessagingInstance } from "../firebase";

// FCM web VAPID key — public by design, same security model as the rest
// of the Firebase web config in src/firebase.js. Hardcoded so cloud
// builds work without env var wiring.
const VAPID_KEY = "BNsA9dNffsHSBOpdZ4YQijKabdOkCZod8-sBFz7IJ9sZKMG3nLF5F6uz3Qh4JUh6BAv04hrZ7Z9hntLf8kcMpjg";
const isNativeApp = () => Capacitor.getPlatform() !== "web";

// Single public entry: NotificationSettingsPage calls this without caring
// about platform. Branches internally — web uses Firebase Messaging SDK +
// VAPID, native uses @capacitor/push-notifications + FCM (token from
// google-services.json registration). Both write to the same fcmTokens
// array on the user doc so the server-side sendPushOnNotification function
// pushes to whichever channel(s) the user has enabled.
export async function enablePushNotifications(uid) {
  if (isNativeApp()) return enablePushNative(uid);
  return enablePushWeb(uid);
}

export async function disablePushNotifications(uid) {
  if (isNativeApp()) return disablePushNative(uid);
  return disablePushWeb(uid);
}

export async function attachForegroundListener(handler) {
  if (isNativeApp()) return attachForegroundListenerNative(handler);
  return attachForegroundListenerWeb(handler);
}

// =============== WEB (Firebase Messaging) ===============

async function enablePushWeb(uid) {
  if (!uid) return { ok: false, reason: "not-signed-in" };
  if (!VAPID_KEY) return { ok: false, reason: "no-vapid" };

  const messaging = await getMessagingInstance();
  if (!messaging) return { ok: false, reason: "unsupported" };

  let permission = Notification.permission;
  if (permission === "default") {
    permission = await Notification.requestPermission();
  }
  if (permission !== "granted") return { ok: false, reason: "denied" };

  try {
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (!token) return { ok: false, reason: "no-token" };
    await updateDoc(doc(db, `users/${uid}`), {
      fcmTokens: arrayUnion(token),
    });
    return { ok: true, token };
  } catch (err) {
    return { ok: false, reason: err.message || "unknown" };
  }
}

async function disablePushWeb(uid) {
  const messaging = await getMessagingInstance();
  if (!messaging) return { ok: false, reason: "unsupported" };
  try {
    const token = await getToken(messaging, { vapidKey: VAPID_KEY });
    if (token && uid) {
      await updateDoc(doc(db, `users/${uid}`), {
        fcmTokens: arrayRemove(token),
      });
    }
    if (token) await deleteToken(messaging);
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err.message || "unknown" };
  }
}

async function attachForegroundListenerWeb(handler) {
  const messaging = await getMessagingInstance();
  if (!messaging) return () => {};
  return onMessage(messaging, (payload) => {
    if (handler) handler(payload);
  });
}

// =============== NATIVE (@capacitor/push-notifications) ===============

// Cache the most-recent native token so disablePushNative can remove
// THIS device's token specifically rather than wiping all tokens (which
// would also kill push on the user's other devices).
let lastNativeToken = null;

async function enablePushNative(uid) {
  if (!uid) return { ok: false, reason: "not-signed-in" };

  const { PushNotifications } = await import("@capacitor/push-notifications");

  const perm = await PushNotifications.requestPermissions();
  if (perm.receive !== "granted") return { ok: false, reason: "denied" };

  // CRITICAL ordering: attach listeners (and await their handles) BEFORE
  // calling register(). If you call register() first the FCM token event
  // can fire before the JS listener is wired up, and we silently miss it
  // — which manifests as "Enable Push" succeeding (timeout never hits
  // because something resolves elsewhere) but fcmTokens stays empty in
  // Firestore. Don't reorder these.
  let regSub = null;
  let errSub = null;

  return new Promise(async (resolve) => {
    let done = false;
    const finish = (result) => {
      if (done) return;
      done = true;
      try { regSub && regSub.remove(); } catch {}
      try { errSub && errSub.remove(); } catch {}
      resolve(result);
    };

    regSub = await PushNotifications.addListener("registration", async (token) => {
      try {
        const value = token?.value || "";
        if (!value) {
          finish({ ok: false, reason: "empty-token" });
          return;
        }
        lastNativeToken = value;
        await updateDoc(doc(db, `users/${uid}`), {
          fcmTokens: arrayUnion(value),
        });
        finish({ ok: true, token: value });
      } catch (err) {
        finish({ ok: false, reason: err.message || "save-failed" });
      }
    });

    errSub = await PushNotifications.addListener("registrationError", (err) => {
      finish({ ok: false, reason: err.error || "register-failed" });
    });

    try {
      await PushNotifications.register();
    } catch (err) {
      finish({ ok: false, reason: err.message || "register-threw" });
    }

    // Safety net — if neither event fires in 30s, give up so the UI button
    // doesn't hang forever.
    setTimeout(() => finish({ ok: false, reason: "registration-timeout" }), 30000);
  });
}

async function disablePushNative(uid) {
  if (!uid) return { ok: false, reason: "not-signed-in" };
  try {
    if (lastNativeToken) {
      await updateDoc(doc(db, `users/${uid}`), {
        fcmTokens: arrayRemove(lastNativeToken),
      });
      lastNativeToken = null;
    } else {
      // We don't know which token belongs to this device — fall back to
      // wiping all of them so push truly stops on every device. User can
      // re-enable per device as needed.
      await updateDoc(doc(db, `users/${uid}`), { fcmTokens: [] });
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: err.message || "unknown" };
  }
}

async function attachForegroundListenerNative(handler) {
  const { PushNotifications } = await import("@capacitor/push-notifications");
  const sub = await PushNotifications.addListener(
    "pushNotificationReceived",
    (notification) => {
      if (handler) handler({ notification });
    }
  );
  return () => sub.remove();
}
